import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from './contexts/LanguageContext';
import LanguageSelector from './components/LanguageSelector';
import { getTierMeta } from './utils/tierMeta';
import { pickQuickplayBotTypes } from './utils/quickplayBots';

function Lobby({ socket, username, onCreateRoom, onJoinRoom, onLogout = () => {}, tiers = null, onStartChallenge = () => {} }) {
  const { t, version } = useLanguage();
  const [rooms, setRooms] = useState([]);
  const [ongoingGames, setOngoingGames] = useState(0);
  const [ongoingPlayers, setOngoingPlayers] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [onlineUserCount, setOnlineUserCount] = useState(0);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const [showBotInfo, setShowBotInfo] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [contactStatus, setContactStatus] = useState(null);
  const [isSubmittingContact, setIsSubmittingContact] = useState(false);
  const chatEndRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(false);
  const menuRef = useRef(null);

  const getApiBase = () => {
    if (process.env.REACT_APP_BACKEND_URL) {
      return process.env.REACT_APP_BACKEND_URL;
    }
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001';
    }
    if (
      window.location.hostname.startsWith('192.168.') ||
      window.location.hostname.startsWith('10.')
    ) {
      return `http://${window.location.hostname}:3001`;
    }
    return '';
  };

  const API_BASE = getApiBase();

  const scrollToBottom = () => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const [isGuestUser, setIsGuestUser] = useState(false);
  const tierLevel = tiers?.tier || 0;
  const tierInfo = getTierMeta(tierLevel);
  const tierLabel = t(tierInfo.labelKey) || tierInfo.defaultLabel;

  // Each mini-game is a challenge reward (MINI-01); nginx checks the same
  // tier on the server before serving the page.
  const canAccessJewels = !isGuestUser && tierLevel >= 1;
  const canAccessMinesweeper = !isGuestUser && tierLevel >= 2;
  const canAccessShadow = !isGuestUser && tierLevel >= 3;

  // Phones show no tooltips, so a tap on a locked game says why it is locked.
  const [eggNotice, setEggNotice] = useState(null);
  // nginx sends a player without the tier back here (/?minigame=locked).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('minigame') !== 'locked') return;
    setEggNotice(t('rules.eggLocked') || 'Complete the challenge tier for this game to unlock it.');
    params.delete('minigame');
    const query = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
  }, [t]);

  useEffect(() => {
    if (!eggNotice) return undefined;
    const timer = setTimeout(() => setEggNotice(null), 4000);
    return () => clearTimeout(timer);
  }, [eggNotice]);

  const getEggLockMessage = (requiredTier) => {
    if (isGuestUser) {
      return t('rules.eggGuest') || 'Log in with an account to access this easter egg.';
    }
    return (t('rules.eggTier') || 'Complete Challenge {{tier}} to unlock this easter egg.').replace('{{tier}}', requiredTier);
  };

  const renderTierBadge = (tier, size = 'md') => {
    const meta = getTierMeta(tier);
    const label = t(meta.labelKey) || meta.defaultLabel;
    const sizeClass = size === 'small' ? 'tier-badge--small' : '';
    return (
      <span
        className={`tier-badge tier-badge-${meta.level} ${sizeClass}`.trim()}
        title={label}
      >
        {meta.icon}
      </span>
    );
  };
  const localizedRulesList = t('rules.list');
  const rulesList = (Array.isArray(localizedRulesList) ? localizedRulesList : [localizedRulesList]).filter(Boolean);

  const tierButtonThemes = {
    1: {
      background: 'linear-gradient(135deg, #8d5321, #b87333)',
      color: '#fff',
      boxShadow: '0 4px 12px rgba(184, 115, 51, 0.35)',
      border: '1px solid rgba(255, 227, 194, 0.3)'
    },
    2: {
      background: 'linear-gradient(135deg, #828282, #c0c0c0)',
      color: '#121212',
      boxShadow: '0 4px 12px rgba(192, 192, 192, 0.4)',
      border: '1px solid rgba(255,255,255,0.35)'
    },
    3: {
      background: 'linear-gradient(135deg, #c19b00, #ffd700)',
      color: '#2b1a01',
      boxShadow: '0 4px 12px rgba(255, 215, 0, 0.4)',
      border: '1px solid rgba(255, 240, 200, 0.8)'
    }
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem('user');
      const parsed = stored ? JSON.parse(stored) : null;
      setIsGuestUser(Boolean(parsed?.isGuest));
      if (parsed?.username) {
        setContactForm(prev => ({ ...prev, name: prev.name || parsed.username }));
      }
      if (parsed?.email) {
        setContactForm(prev => ({ ...prev, email: prev.email || parsed.email }));
      }
    } catch (err) {
      setIsGuestUser(false);
    }
  }, [username]);

  const createEasterEggHandler = (eggKey, url) => async (event) => {
    event.preventDefault();
    const payload = JSON.stringify({ eggKey });
    const endpoint = `${API_BASE}/api/easter-eggs/click`;
    const openLink = () => window.open(url, '_blank', 'noopener,noreferrer');

    try {
      if (navigator?.sendBeacon) {
        const blob = new Blob([payload], { type: 'application/json' });
        navigator.sendBeacon(endpoint, blob);
        openLink();
        return;
      }

      await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true
      });
      openLink();
    } catch (error) {
      console.error('Failed to record easter egg click', error);
      openLink();
    }
  };

  const openContactModal = () => {
    setShowContactModal(true);
    setContactStatus(null);
    if ((!contactForm.name || !contactForm.email) && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem('user');
        const parsed = stored ? JSON.parse(stored) : null;
        if (parsed?.username || parsed?.email) {
          setContactForm(prev => ({
            ...prev,
            name: prev.name || parsed?.username || '',
            email: prev.email || parsed?.email || ''
          }));
        }
      } catch (err) {
        // ignore
      }
    }
  };

  const handleContactChange = (field) => (event) => {
    const value = event.target.value;
    setContactForm(prev => ({ ...prev, [field]: value }));
  };

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    setContactStatus(null);

    if (!contactForm.name || !contactForm.email || !contactForm.message) {
      setContactStatus({ type: 'error', text: 'Please fill in all fields.' });
      return;
    }

    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      setContactStatus({ type: 'error', text: 'You need to be logged in to send a message.' });
      return;
    }

    setIsSubmittingContact(true);
    try {
      const response = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(contactForm)
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error?.msg || 'Failed to send your message.');
      }

      setContactStatus({ type: 'success', text: 'Message sent! Thanks for reaching out.' });
      setContactForm(prev => ({ ...prev, message: '' }));
    } catch (error) {
      setContactStatus({ type: 'error', text: error.message || 'Failed to send your message.' });
    } finally {
      setIsSubmittingContact(false);
    }
  };

  useEffect(() => {
    // Only auto-scroll if explicitly enabled (after user interaction)
    if (shouldAutoScroll) {
      scrollToBottom();
    }
  }, [chatMessages, shouldAutoScroll]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Request room list when component mounts
    socket.emit('getRooms');

    // Listen for room list updates (real-time updates from server)
    socket.on('roomsList', (data) => {
      console.log('🏠 LOBBY: Received real-time roomsList update:', data);
      
      // Handle both old array format and new object format
      if (Array.isArray(data.rooms)) {
        console.log('🏠 LOBBY: Setting rooms from data.rooms:', data.rooms.length);
        setRooms(data.rooms);
      } else if (Array.isArray(data)) {
        // Old format - data is directly the rooms array
        console.log('🏠 LOBBY: Setting rooms from data array:', data.length);
        setRooms(data);
      } else {
        console.warn('⚠️ LOBBY: Unexpected roomsList data format:', data);
        setRooms([]);
      }
      setOngoingGames(data.ongoingGames || 0);
      setOngoingPlayers(data.ongoingPlayers || 0);
      setLoading(false);
    });

    // Listen for errors
    socket.on('error', (data) => {
      alert('Error: ' + data.message);
      setLoading(false);
    });

    // Listen for lobby chat messages
    socket.on('lobbyChatMessage', (data) => {
      setChatMessages(prev => [...prev, { ...data, tier: data?.tier ?? 0 }]);
    });

    // Listen for online user count updates
    socket.on('onlineUserCount', (data) => {
      console.log('👥 LOBBY: Received user count update:', data.count);
      setOnlineUserCount(data.count);
    });

    return () => {
      socket.off('roomsList');
      socket.off('error');
      socket.off('lobbyChatMessage');
      socket.off('onlineUserCount');
    };
  }, [socket]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!loading) {
        console.log('🔄 LOBBY: Fallback polling for room list');
        socket.emit('getRooms');
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [socket, loading]);

  const handleCreateRoom = () => {
    setLoading(true);
    console.log('Creating room with socket.id:', socket.id);
    socket.emit('createRoom', { username });
    
    socket.once('roomCreated', (data) => {
      console.log('Room created:', data);
      onCreateRoom(data.roomId, data.roomInfo);
    });
  };

  const handleJoinRoom = (roomId) => {
    setLoading(true);
    socket.emit('joinRoom', { roomId, username });
    onJoinRoom(roomId);
  };

  const handleSendMessage = (e) => {
    e.preventDefault();
    if (chatInput.trim()) {
      socket.emit('lobbyChatMessage', { message: chatInput, tier: tierLevel });
      setChatInput('');
      setShouldAutoScroll(true); // Enable auto-scroll when user sends a message
    }
  };

  // Generate consistent color for usernames
  const getUsernameColor = (username) => {
    const colors = ['#66BB6A', '#2196F3', '#AB47BC', '#FF7043', '#00E676', '#FFCA28', '#EF5350'];
    let hash = 0;
    for (let i = 0; i < username.length; i++) {
      hash = username.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  // Convert room ID to display name (e.g., "lightning-falcon-ke9k" -> "Lightning Falcon")
  const getRoomDisplayName = (roomId) => {
    // Handle old format room-1234567890
    if (roomId.startsWith('room-')) {
      return roomId;
    }
    
    // Handle new format: "lightning-falcon-ke9k" -> "Lightning Falcon"
    // Split by dash and take only first two parts (ignore hash suffix)
    const parts = roomId.split('-');
    if (parts.length >= 2) {
      const adjective = parts[0].charAt(0).toUpperCase() + parts[0].slice(1);
      const animal = parts[1].charAt(0).toUpperCase() + parts[1].slice(1);
      return `${adjective} ${animal}`;
    }
    
    return roomId; // Fallback to original ID
  };

  const handleQuickPlay = () => {
    setLoading(true);
    console.log('Starting quickplay with socket.id:', socket.id);
    socket.emit('createRoom', { username });
    
    socket.once('roomCreated', (data) => {
      console.log('Quickplay room created:', data);
      const roomId = data.roomId;
      
      const botTypes = pickQuickplayBotTypes();
      console.log('🎲 Quickplay bot types selected:', botTypes.join(', '));
      
      socket.emit('addBot', { roomId, slotIndex: 1, botType: botTypes[0] });
      socket.emit('addBot', { roomId, slotIndex: 2, botType: botTypes[1] });
      socket.emit('addBot', { roomId, slotIndex: 3, botType: botTypes[2] });
      
      // Start game shortly after bots join (host defaults to ready)
      setTimeout(() => {
        socket.emit('startGame', { roomId });
      }, 1000);
      
      onCreateRoom(data.roomId, data.roomInfo);
    });
  };

  return (
    <div className="lobby-container">
      <div
        ref={menuRef}
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          zIndex: 1100,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end'
        }}
      >
        <button
          type="button"
          onClick={() => setIsMenuOpen((prev) => !prev)}
          style={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.4)',
            background: 'rgba(0,0,0,0.5)',
            color: '#fff',
            fontSize: 20,
            lineHeight: 1,
            cursor: 'pointer'
          }}
          aria-label="Open menu"
        >
          ☰
        </button>
        {isMenuOpen && (
          <div
            style={{
              marginTop: 8,
              background: 'rgba(0,0,0,0.75)',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: 12,
              padding: '10px 14px',
              minWidth: 180,
              boxShadow: '0 8px 20px rgba(0,0,0,0.4)'
            }}
          >
            <button
              type="button"
              onClick={() => { setShowRules(true); setIsMenuOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: 8
              }}
            >
              ℹ️ {t('rules.button') || 'How to play'}
            </button>
            <button
              type="button"
              onClick={() => { setShowBotInfo(true); setIsMenuOpen(false); }}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.08)',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginBottom: 8
              }}
            >
              🤖 {t('bots.button') || 'Bot AI Explained'}
            </button>
            <LanguageSelector
              style={{
                position: 'static',
                marginBottom: 10,
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '6px'
              }}
            />
            <button
              type="button"
              onClick={onLogout}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 8,
                border: 'none',
                background: '#f44336',
                color: '#fff',
                fontWeight: 'bold',
                cursor: 'pointer'
              }}
            >
              {t('common.logout') || 'Logout'}
            </button>
          </div>
        )}
      </div>
      <h2>{t('lobby.title')}</h2>
      <p style={{ textAlign: 'center', marginBottom: '10px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <span className="tier-badge-wrapper">
          {t('lobby.welcome', { username })}
          {renderTierBadge(tierLevel)}
        </span>
      </p>
      
      <div className="lobby-actions">
        <div className="lobby-actions-wrapper">
          <button
            onClick={handleQuickPlay}
            disabled={loading}
            className="quickplay-btn"
            style={{
              backgroundColor: loading ? '#cccccc' : '#4CAF50',
              color: 'white',
              padding: '15px 30px',
              fontSize: '18px',
              border: 'none',
              borderRadius: '8px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: 'bold',
              transition: 'background-color 0.3s'
            }}
            onMouseEnter={(e) => {
              if (!loading) e.target.style.backgroundColor = '#45a049';
            }}
            onMouseLeave={(e) => {
              if (!loading) e.target.style.backgroundColor = '#4CAF50';
            }}
          >
            {t('lobby.quickPlay')}
          </button>
          <button
            onClick={handleCreateRoom}
            disabled={loading}
            className="create-room-btn"
          >
            {t('lobby.createRoom')}
          </button>
        </div>
        <div className="lobby-challenges">
          <div className="challenge-tier-banner" style={{ borderColor: tierInfo.color, color: '#fff', background: 'rgba(0,0,0,0.4)' }}>
            {(t('lobby.currentTier') || 'Current Tier')}: <strong>{tierLabel}</strong>
          </div>
          <div className="challenge-buttons">
            {[1, 2, 3].map(challenge => {
              const earnedTier = tiers?.tier || 0;
              const unlocked = challenge === 1 ? true : earnedTier >= (challenge - 1);
              const details = {
                1: {
                  label: 'Copper Trials',
                  description: 'Beat the smart squad (you + veteran vs 2 smart bots).',
                  reward: 'Earn the Copper badge + Jewels access.'
                },
                2: {
                  label: 'Silver Gauntlet',
                  description: 'Beat the veteran squad (you + veteran vs 2 veterans).',
                  reward: 'Earn the Silver badge + Minesweeper access.'
                },
                3: {
                  label: 'Golden Crown',
                  description: 'Beat the strongest bots (you + veteran vs 2 masters).',
                  reward: 'Earn the Gold crown + Shadowruler access.'
                }
              }[challenge];
              const tooltip = unlocked
                ? `${details?.description || ''} ${details?.reward || ''}`.trim()
                : `Complete Challenge ${challenge - 1} to unlock this tier.`;
              const theme = tierButtonThemes[challenge] || {};
              const buttonStyles = {
                background: unlocked ? theme.background || '#ff9800' : '#555',
                color: unlocked ? theme.color || '#220b00' : '#bbb',
                cursor: unlocked ? 'pointer' : 'not-allowed',
                border: unlocked ? theme.border || '1px solid rgba(255,255,255,0.3)' : '1px solid rgba(255,255,255,0.3)',
                borderRadius: '6px',
                padding: '10px 12px',
                fontWeight: 'bold',
                height: 48,
                flex: '1 1 0',
                minWidth: 100,
                boxShadow: unlocked ? theme.boxShadow || 'none' : 'none',
                filter: unlocked ? 'brightness(1)' : 'grayscale(0.4)',
                opacity: unlocked ? 1 : 0.55
              };
              const statusIcon = !unlocked ? '🔒 ' : earnedTier >= challenge ? '✅ ' : '';
              return (
                <button
                  key={challenge}
                  style={buttonStyles}
                  disabled={!unlocked || loading}
                  onClick={() => unlocked && onStartChallenge(challenge)}
                  title={tooltip}
                >
                  {`${statusIcon}Tier ${challenge}`}<br />
                  <span style={{ fontSize: '0.75em', fontWeight: 500 }}>{details?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>
      
      <div className="rooms-list">
        <h3>{t('lobby.availableRooms')}</h3>
        {rooms.length === 0 ? (
          <div className="no-rooms">
            {ongoingGames > 0 ? (
              <div>
                <p>{t('lobby.gamesInProgress', { 
                  count: ongoingGames, 
                  plural: ongoingGames > 1 ? 's' : '', 
                  players: ongoingPlayers,
                  playerPlural: ongoingPlayers > 1 ? 's' : ''
                })}</p>
                <p>{t('lobby.createNewRoom')}</p>
              </div>
            ) : (
              <p>{t('lobby.noRooms')}</p>
            )}
          </div>
        ) : (
          <div className="room-cards">
            {rooms.map((room) => (
              <div key={room.roomId} className="room-card">
                <div className="room-info">
                  <h4>{getRoomDisplayName(room.roomId)}</h4>
                  <p>👑 {room.hostUsername}</p>
                  <p>Players: {room.playerCount}/{room.maxPlayers}</p>
                  <p className="room-status">{room.status}</p>
                </div>
                <button
                  onClick={() => handleJoinRoom(room.roomId)}
                  disabled={loading || room.playerCount >= room.maxPlayers}
                  className="join-btn"
                >
                  {room.playerCount >= room.maxPlayers ? t('lobby.full') : t('lobby.join')}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="lobby-bottom-section">
        <div className="lobby-chat">
        <h3 style={{
          margin: '0 0 15px 0',
          color: 'white',
          fontSize: '1.2em',
          textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <span>💬 Lobby Chat</span>
          <span style={{
            fontSize: '0.8em',
            color: '#4CAF50',
            fontWeight: 'normal',
            background: 'rgba(76, 175, 80, 0.2)',
            padding: '4px 8px',
            borderRadius: '12px',
            border: '1px solid rgba(76, 175, 80, 0.3)'
          }}>
            👥 {onlineUserCount} online
          </span>
        </h3>
        <div className="chat-messages" style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: 'rgba(0,0,0,0.4)',
          borderRadius: '10px',
          padding: '10px',
          marginBottom: '10px',
          minHeight: '96px', // 4 lines of text (24px line height * 4)
          border: '1px solid rgba(255,255,255,0.1)'
        }}>
          {chatMessages.length === 0 ? (
            <p style={{ 
              color: 'rgba(255,255,255,0.6)', 
              fontStyle: 'italic',
              textAlign: 'center',
              marginTop: '20px',
              textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
            }}>
              {t('lobby.chat.noMessages')}
            </p>
          ) : (
            chatMessages.map((msg, index) => (
              <div
                key={index}
                style={{
                  marginBottom: '8px',
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: '6px',
                  opacity: msg.type === 'system' ? 0.8 : 1
                }}
              >
                {msg.type === 'system' ? (
                  <>
                    <span style={{
                      color: '#FFD700',
                      fontWeight: 'bold',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}>
                      ⚡
                    </span>
                    <span style={{
                      color: '#FFE082',
                      flex: 1,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                      fontStyle: 'italic'
                    }}>
                      {msg.message}
                    </span>
                  </>
                ) : (
                  <>
                    {renderTierBadge(msg?.tier ?? 0, 'small')}
                    <span style={{
                      color: getUsernameColor(msg.username),
                      fontWeight: 'bold',
                      textShadow: '1px 1px 2px rgba(0,0,0,0.5)'
                    }}>
                      {msg.username}
                    </span>
                    <span style={{
                      color: '#ffffff',
                      flex: 1,
                      textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                    }}>
                      {msg.message}
                    </span>
                  </>
                )}
                <span style={{
                  fontSize: '0.75em',
                  color: 'rgba(255,255,255,0.6)',
                  textShadow: '1px 1px 2px rgba(0,0,0,0.8)'
                }}>
                  {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            ))
          )}
          <div ref={chatEndRef} />
        </div>
        <form onSubmit={handleSendMessage} style={{ 
          display: 'flex',
          gap: '10px'
        }}>
          <input
            type="text"
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder={t('lobby.chat.placeholder')}
            style={{
              flex: 1,
              padding: '10px 15px',
              borderRadius: '25px',
              border: '1px solid rgba(255,255,255,0.2)',
              backgroundColor: 'rgba(255,255,255,0.08)',
              color: 'white',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.3s'
            }}
            className="lobby-chat-input"
            onFocus={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.12)';
              e.target.style.borderColor = '#4CAF50';
            }}
            onBlur={(e) => {
              e.target.style.backgroundColor = 'rgba(255,255,255,0.08)';
              e.target.style.borderColor = 'rgba(255,255,255,0.2)';
            }}
          />
          <button
            type="submit"
            style={{
              padding: '10px 20px',
              borderRadius: '25px',
              border: 'none',
              background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
              color: 'white',
              cursor: 'pointer',
              fontWeight: 'bold',
              fontSize: '14px',
              transition: 'transform 0.2s',
              boxShadow: '0 4px 15px 0 rgba(76, 175, 80, 0.3)'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            {t('lobby.chat.send')}
          </button>
        </form>
        </div>

        {/* TODO: Re-enable Online Players when backend supports it */}
      </div>

      <div className="lobby-easter-egg-footer">
        <div className="lobby-easter-egg-inner">
          <div className="lobby-footer-actions">
            <button
              type="button"
              className="lobby-legal-button"
              onClick={() => window.location.assign('/legal')}
              title="Legal documents"
              aria-label="Legal documents"
            >
              📄
            </button>
            <button
              type="button"
              className="lobby-legal-button"
              onClick={openContactModal}
              title="Contact admin"
              aria-label="Contact admin"
            >
              ✉️
            </button>
          </div>
          <div className="lobby-easter-egg-links">
            <a
              className={`lobby-easter-egg-link lobby-easter-egg-link--jewels ${canAccessJewels ? '' : 'guest-locked'}`}
              href={canAccessJewels ? '/jewels/' : '#tier-lock'}
              target={canAccessJewels ? '_blank' : undefined}
              rel={canAccessJewels ? 'noopener noreferrer' : undefined}
              title={canAccessJewels ? '💎 Jewels game' : getEggLockMessage(1)}
              style={{ opacity: canAccessJewels ? 1 : 0.4, pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={canAccessJewels ? createEasterEggHandler('jewels', '/jewels/') : (e) => {
                e.preventDefault();
                setEggNotice(getEggLockMessage(1));
              }}
            >
              💎
            </a>
            <a
              className={`lobby-easter-egg-link lobby-easter-egg-link--minesweeper ${canAccessMinesweeper ? '' : 'guest-locked'}`}
              href={canAccessMinesweeper ? '/minesweeper/' : '#tier-lock'}
              target={canAccessMinesweeper ? '_blank' : undefined}
              rel={canAccessMinesweeper ? 'noopener noreferrer' : undefined}
              title={canAccessMinesweeper ? '💣 Minesweeper game' : getEggLockMessage(2)}
              style={{ opacity: canAccessMinesweeper ? 1 : 0.4, pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={canAccessMinesweeper ? createEasterEggHandler('minesweeper', '/minesweeper/') : (e) => {
                e.preventDefault();
                setEggNotice(getEggLockMessage(2));
              }}
            >
              💣
            </a>
            <span className="lobby-easter-egg-version">v{version}</span>
            <a
              className={`lobby-easter-egg-link lobby-easter-egg-link--crown ${canAccessShadow ? '' : 'guest-locked'}`}
              href={canAccessShadow ? '/shadowruler/' : '#tier-lock'}
              target={canAccessShadow ? '_blank' : undefined}
              rel={canAccessShadow ? 'noopener noreferrer' : undefined}
              title={canAccessShadow ? '🤫 Secret Shadowruler game' : getEggLockMessage(3)}
              style={{ opacity: canAccessShadow ? 1 : 0.4, pointerEvents: 'auto', cursor: 'pointer' }}
              onClick={canAccessShadow ? createEasterEggHandler('shadowruler', '/shadowruler/') : (e) => {
                e.preventDefault();
                setEggNotice(getEggLockMessage(3));
              }}
            >
              🏰
            </a>
          </div>
        </div>
      </div>
      {eggNotice && (
        <div className="lobby-easter-egg-notice" role="status">{eggNotice}</div>
      )}

      {showRules && (
        <div className="rules-modal-overlay" onClick={() => setShowRules(false)}>
          <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('rules.title') || 'How to play'}</h3>
            <p>{t('rules.intro')}</p>
            <p>{t('rules.summary')}</p>
            <ul>
              {rulesList.map((entry, idx) => {
                if (entry && typeof entry === 'object' && Array.isArray(entry.items)) {
                  return (
                    <li key={`${entry.title || idx}-section`}>
                      <strong>{entry.title}</strong>
                      <ul className="rules-sublist">
                        {entry.items.map((bullet, bulletIdx) => (
                          <li key={`${entry.title || idx}-bullet-${bulletIdx}`}>{bullet}</li>
                        ))}
                      </ul>
                    </li>
                  );
                }
                return <li key={`rule-${idx}`}>{entry}</li>;
              })}
            </ul>
            <button
              type="button"
              className="rules-close-btn"
              onClick={() => setShowRules(false)}
            >
              {t('rules.close') || 'Close'}
            </button>
          </div>
        </div>
      )}

      {showBotInfo && (
        <div className="rules-modal-overlay" onClick={() => setShowBotInfo(false)}>
          <div className="rules-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('bots.title') || 'Bot AI & Difficulty Explained'}</h3>

            <p>{t('bots.intro') || 'The 6 available bots are:'}</p>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: '#3b82f6', marginBottom: 10 }}>
                {t('bots.ruleBasedTitle') || '3 Rule-Based Bots (Heuristics)'}
              </h4>
              <ul>
                <li>
                  <strong>{t('bots.beginner.name') || 'Beginner'}:</strong>{' '}
                  {t('bots.beginner.desc') || 'Plays defensively, is not very competitive'}
                </li>
                <li>
                  <strong>{t('bots.aggro.name') || 'Aggro'}:</strong>{' '}
                  {t('bots.aggro.desc') || 'Plays aggressively, will trump immediately'}
                </li>
                <li>
                  <strong>{t('bots.smart.name') || 'Smart'}:</strong>{' '}
                  {t('bots.smart.desc') || 'More well-rounded approach, blending defensive and opportunistic plays'}
                </li>
              </ul>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: '#8b5cf6', marginBottom: 10 }}>
                {t('bots.mctsTitle') || '2 "Thinking" Bots (IS-MCTS)'}
              </h4>
              <p style={{ fontSize: 14, marginBottom: 10 }}>
                {t('bots.mctsExplain') || 'These bots use Monte Carlo simulations. They keep track of which cards were played and which suits players could not follow, then reason about different "futures" and pick the card most likely to win.'}
              </p>
              <ul>
                <li>
                  <strong>{t('bots.veteran.name') || 'Veteran'}:</strong>{' '}
                  {t('bots.veteran.desc') || 'Uses an aggressive thinking approach, prioritising short term wins'}
                </li>
                <li>
                  <strong>{t('bots.master.name') || 'Master'}:</strong>{' '}
                  {t('bots.master.desc') || 'Samples the widest range of hands the other players could be holding and picks the card that comes out best across all of them. The strongest bot at the table.'}
                </li>
              </ul>
            </div>

            <div style={{ marginBottom: 20 }}>
              <h4 style={{ color: '#e64a19', marginBottom: 10 }}>
                {t('bots.jevTitle') || '1 Language-Model Bot'}
              </h4>
              <p style={{ fontSize: 14, marginBottom: 10 }}>
                {t('bots.jevExplain') || 'This bot is handed the state of the hand in words and asked which of the legal cards to play, and whether to accept the trump. It reasons about the position rather than searching through it, so it plays a recognisably human game: usually sensible, sometimes inspired, sometimes loose.'}
              </p>
              <ul>
                <li>
                  <strong>{t('bots.jev.name') || 'JEV'}:</strong>{' '}
                  {t('bots.jev.desc') || 'Reasons about the hand in plain language instead of calculating it. Plays like a person rather than a solver.'}
                </li>
              </ul>
            </div>

            <p style={{ fontSize: 14, marginTop: 20, padding: 12, background: 'rgba(59,130,246,0.1)', borderRadius: 8 }}>
              {t('bots.honesty') || 'The bots do not cheat, they can only see their own cards and the game board state.'}
            </p>

            <p style={{ fontSize: 13, marginTop: 16, fontStyle: 'italic', color: 'rgba(15,23,42,0.6)' }}>
              {t('bots.development') || 'These IS-MCTS bots were selected as the 3 highest performing bots from tournaments against the rule-based bots and other bots created through RL (Reinforcement Learning) and GA (Genetic Algorithms). The bots are not yet as strong as they could be but improving them further requires a sophisticated approach and lots of time testing and tuning.'}
            </p>

            <p style={{ fontSize: 13, marginTop: 12, fontStyle: 'italic', color: 'rgba(15,23,42,0.6)' }}>
              {t('bots.feedback') || 'Feel free to provide your experience about the bots, there is a feedback button at the bottom of the page. Let me know if you want me to pursue trying to create a superhuman kwajongen bot.'}
            </p>

            <button
              type="button"
              className="rules-close-btn"
              onClick={() => setShowBotInfo(false)}
            >
              {t('bots.close') || 'Close'}
            </button>
          </div>
        </div>
      )}

      {showContactModal && (
        <div className="contact-modal-overlay" onClick={() => setShowContactModal(false)}>
          <div className="contact-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Contact the Kwajongen Team</h3>
            <p style={{ marginBottom: 16, color: 'rgba(15,23,42,0.75)' }}>
              Share feedback, report issues, or ask for help. We read every message.
            </p>
            <form onSubmit={handleContactSubmit} className="contact-form">
              <label className="contact-form-group">
                <span>Your Name</span>
                <input type="text" value={contactForm.name} onChange={handleContactChange('name')} placeholder="Jef de Kwajongen" required />
              </label>
              <label className="contact-form-group">
                <span>Email</span>
                <input type="email" value={contactForm.email} onChange={handleContactChange('email')} placeholder="you@example.com" required />
              </label>
              <label className="contact-form-group">
                <span>Message</span>
                <textarea value={contactForm.message} onChange={handleContactChange('message')} rows="4" placeholder="Let us know how we can help" required />
              </label>
              {contactStatus && (
                <div className={`contact-status ${contactStatus.type === 'success' ? 'contact-status--success' : 'contact-status--error'}`}>
                  {contactStatus.text}
                </div>
              )}
              <div className="contact-form-actions">
                <button type="button" className="rules-close-btn" onClick={() => setShowContactModal(false)}>
                  Close
                </button>
                <button type="submit" className="primary-btn" disabled={isSubmittingContact}>
                  {isSubmittingContact ? 'Sending…' : 'Send Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Lobby;
